import { useCallback, useMemo, useState } from 'react';
import { useScoringCategories } from '@/features/constructorScoringCategory/hooks/useScoringCategories';
import { queryClient } from '@/app/QueryClientProvider';
import { adminLocalizationKeys } from '@/api';
import { ScoringConstructorApi } from '@/features/constructorScoringCategory/api';
import { usePopups } from '@/hooks';

export const useScoringCategory = (id) => {
  const { showErrorToast, showSuccessToast } = usePopups();

  const { data, isError, refetch } = useScoringCategories();

  const scoringCategoryData = useMemo(() => {
    if (!data || !data?.length) return undefined;

    return data.find((item) => item.id === id);
  }, [id, data]);

  const [isUpdating, setIsUpdating] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const handleCreate = useCallback(async (category) => {
    try {
      setIsUpdating(true);
      const result = await ScoringConstructorApi.createScoringCategory(category);
      if (result) {
        await queryClient.refetchQueries({
          queryKey: [adminLocalizationKeys.getAllScoringCategories]
        });
        showSuccessToast('Created');
      }

      return result;
    } catch (e) {
      showErrorToast(e, true);
    } finally {
      setIsUpdating(false);
    }
  }, []);

  const handleUpdate = useCallback(async (category) => {
    try {
      setIsUpdating(true);
      const result = await ScoringConstructorApi.updateScoringCategory(category);
      if (result) {
        await queryClient.refetchQueries({
          queryKey: [adminLocalizationKeys.getAllScoringCategories]
        });
        showSuccessToast('Saved');
      }
    } catch (e) {
      showErrorToast(e, true);
    } finally {
      setIsUpdating(false);
    }
  }, []);

  const handleDelete = useCallback(async (category) => {
    try {
      setIsDeleting(true);
      const result = await ScoringConstructorApi.deleteScoringCategory(category);
      if (result) {
        await queryClient.refetchQueries({
          queryKey: [adminLocalizationKeys.getAllScoringCategories]
        });
        showSuccessToast('Deleted');
      }
    } catch (e) {
      showErrorToast(e, true);
    } finally {
      setIsDeleting(false);
    }
  }, []);

  return {
    data: scoringCategoryData,
    isUpdating,
    isDeleting,
    isError,
    handleCreate,
    handleUpdate,
    handleDelete,
    refetch
  };
};
